import { CheckedState } from "@radix-ui/react-checkbox";
import { RandomId } from "@swivl-mono/helpers";
import { NodeInterface, VarKey } from "@swivl-mono/types";
import { Checkbox, RadioButtons } from "@swivl-mono/ui";
import React from "react";

import UserVariablesDropdown from "../../../../../Components/Dropdowns/UserVariablesDropdown/UserVariablesDropdown";
import { TagInput, TagInputType } from "../../../../../Components/TagInput/TagInput";
import VarsModel from "../../../../../Models/Variables/VarsModel.Model";
import TabNodeLayoutData from "../../NodeLayouts/TabNodeLayoutData";
import InspectorFooter from "../../SidePanel/Inspector/components/InspectorFooter";
import InspectorNodeHeader from "../../SidePanel/Inspector/components/InspectorNodeHeader";
import NodePanel from "../NodePanel";

const ValidateNormalize = {
  Type: "validateNormalize",
  Empty: function () {
    return {
      id: RandomId(6),
      type: ValidateNormalize.Type,
      inputValue: `#*${VarKey.UserLastInput}*#`,
      expectedType: "name", // Default to name
      saveToVariableId: "", // Default to empty (no variable selected)
      validGoto: null,
      invalidGoto: null,
      numberParserOptions: {}
    };
  },

  PlumbData: function (node: NodeInterface) {
    const inputDisplay =
      node.inputValue && node.inputValue.length > 0 ? VarsModel.replaceVariableNameInString(node.inputValue, "*", "*") : "No input";

    const displayText = inputDisplay.length > 25 ? inputDisplay.substring(0, 22) + "..." : inputDisplay;

    return TabNodeLayoutData({
      node: node,
      label: "Validate & Normalize",
      title: node.expectedType ? node.expectedType.toUpperCase() : "NAME",
      body: displayText,
      baseWidth: 200,
      tabs: [
        {
          id: "validGoto",
          source: "validGoto",
          target: node.validGoto,
          title: "Valid"
        },
        {
          id: "invalidGoto",
          source: "invalidGoto",
          target: node.invalidGoto,
          title: "Invalid"
        }
      ]
    });
  },

  InspectorPanel: class ValidateNormalizeInspectorPanel extends NodePanel {
    constructor(props: any) {
      super(props);
      this.updateExpectedType = this.updateExpectedType.bind(this);
      this.saveToggled = this.saveToggled.bind(this);
    }

    updateExpectedType(event: any) {
      const newType = event.target.value;
      let node = JSON.parse(JSON.stringify(this.props.node));
      node.expectedType = newType;
      // Reset goto paths when type changes
      node.validGoto = "";
      node.invalidGoto = "";
      this.props.updateNode(node, this.props.node, true);
    }

    saveToggled(checked: CheckedState) {
      if (!checked) {
        this.onChangeUpdateAndRedraw({ target: { name: "saveToVariableId", value: "" } });
        return;
      }

      let vars = VarsModel.variableOptions(true);

      if (!vars || vars.length === 0) {
        alert("User Variables Not Loaded");
        return;
      }

      // Set a default variable based on expected type
      let defaultVar;
      for (let i = 0; i < vars.length; i++) {
        const val = vars[i].value;
        if (
          (val === VarKey.UserName && this.props.node.expectedType === "name") ||
          (val === VarKey.UserEmail && this.props.node.expectedType === "email") ||
          (val === VarKey.UserPhoneNumber && this.props.node.expectedType === "phone") ||
          (val === VarKey.UserPostalCode && this.props.node.expectedType === "zip") ||
          (val === VarKey.UserEntityNumber && this.props.node.expectedType === "number")
        ) {
          defaultVar = vars[i].value;
          break;
        }
      }

      if (!defaultVar) {
        defaultVar = vars[0].value;
      }

      this.onChangeUpdateAndRedraw({ target: { name: "saveToVariableId", value: defaultVar } });
    }

    render() {
      const { node } = this.props;

      // Variable saving UI
      const isSaveTo = node.saveToVariableId && node.saveToVariableId.length > 0;
      const saveToCheckbox = (
        <div className="urlTarget">
          <span>Save normalized value to variable</span>
          <Checkbox onCheckedChange={this.saveToggled} checked={isSaveTo} />
        </div>
      );

      const saveToDropdown = isSaveTo ? (
        <UserVariablesDropdown onChange={this.onChange} skipUserId={true} name="saveToVariableId" value={node.saveToVariableId} />
      ) : null;

      return (
        <div>
          <div className="panel message_panel">
            <InspectorNodeHeader title="Validate & Normalize" />

            <div className="panelRow">
              <TagInput
                label="Input to validate:"
                type={TagInputType.SingleLine}
                value={node.inputValue || ""}
                onChange={this.onChange}
                name="inputValue"
                placeholder="Enter text"
              />
            </div>

            <div className="panelRow">
              <label>Expected type:</label>
              <RadioButtons
                options={[
                  { value: "name", label: "Name" },
                  { value: "email", label: "Email" },
                  { value: "phone", label: "Phone" },
                  { value: "zip", label: "ZIP/Postal Code" },
                  { value: "number", label: "Number" }
                ]}
                name="expectedType"
                value={node.expectedType || "name"}
                onChange={this.updateExpectedType}
              />
            </div>
            {node.expectedType === "number" && (
              <div className="panelRow">
                <label>Number Options:</label>
                <div className="numberOptionsContainer">
                  <div>
                    <label>Minimum Value:</label>
                    <input
                      type="number"
                      name="numberParserOptions.min"
                      value={node.numberParserOptions?.min || ""}
                      onChange={this.onChange}
                      placeholder="No minimum"
                    />
                  </div>
                  <div>
                    <label>Maximum Value:</label>
                    <input
                      type="number"
                      name="numberParserOptions.max"
                      value={node.numberParserOptions?.max || ""}
                      onChange={this.onChange}
                      placeholder="No maximum"
                    />
                  </div>
                  <div>
                    <label>
                      <input
                        type="checkbox"
                        name="numberParserOptions.integer"
                        checked={node.numberParserOptions?.integer || false}
                        onChange={(e) => this.onChange({ target: { name: "numberParserOptions.integer", value: e.target.checked } })}
                      />
                      Integer Only
                    </label>
                  </div>
                  <div>
                    <label>Default Value (if no number found):</label>
                    <input
                      type="number"
                      name="numberParserOptions.defaultValue"
                      value={node.numberParserOptions?.defaultValue || ""}
                      onChange={this.onChange}
                      placeholder="No default"
                    />
                  </div>
                </div>
              </div>
            )}

            {saveToCheckbox}
            {saveToDropdown}
          </div>

          <div className="carouselPadding"></div>
          <InspectorFooter
            deleteNode={this.deleteNode}
            node={this.props.node}
            duplicateNode={this.props.duplicateNode}
            canDuplicate={this.props.canDuplicate}
          />
        </div>
      );
    }
  }
};

export default ValidateNormalize;
